<template>
  <div class="feature-section">
    <div class="container">
      <h2 class="section-title">{{ title }}</h2>
      
      <div class="feature-content">
        <div class="carousel-wrapper">
          <div class="carousel-container">
            <div class="carousel-item" v-for="(image, index) in carouselImages" :key="index" :class="{ active: currentIndex === index }">
              <img :src="image.src" alt="Feature slide" />
              <span class="slide-indicator">{{ index + 1 }}/{{ carouselImages.length }}</span>
            </div>
            <div class="carousel-controls">
              <button class="prev-btn" @click="prevSlide">&#10094;</button>
              <button class="next-btn" @click="nextSlide">&#10095;</button>
            </div>
            <div class="carousel-dots">
              <span v-for="(image, index) in carouselImages" :key="'dot-'+index" 
                    class="dot" :class="{ active: currentIndex === index }"
                    @click="currentIndex = index"></span>
            </div>
          </div>
        </div>

        <div class="feature-list">
          <div class="feature-item" v-for="(feature, index) in features" :key="index">
            <div class="feature-icon">{{ feature.icon }}</div>
            <div class="feature-content">
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FeatureIntro',
  data() {
    return {
      title: 'Feature Introduction',
      currentIndex: 0,
      carouselImages: [
        { src: require('../assets/slide1.png') },
        { src: require('../assets/slide2.png') },
        { src: require('../assets/slide3.png') },
        { src: require('../assets/slide4.png') },
        { src: require('../assets/slide5.png') }
      ],
      features: [
        {
          icon: '🔍',
          title: '【photo enhancer】',
          description: 'Revive old photos with AI photo sharpener that enhances and restores blurry images to high-definition quality.'
        },
        {
          icon: '✨',
          title: '【ai upscale】',
          description: 'Upgrade vintage or old phone photos to look like they were taken with the latest devices, and convert blurred photos to clear digital images.'
        },
        {
          icon: '🔎',
          title: '【image upscaler】',
          description: 'Revive compressed, damaged, or low-resolution photos with free intelligent restoration, including deblurring for high-resolution, high-definition results.'
        },
        {
          icon: '🖼️',
          title: '【image enhancer】',
          description: 'Easily restore damaged photos such as scratches and creases; Remove noise and pixels from photos, eliminating noise in photos while preserving low-resolution images.'
        },
        {
          icon: '📸',
          title: '【image quality enhancer】',
          description: 'Improve photo quality with AI-powered noise reduction and smoothing, colorize old photos in high-definition, and remove blur with Remini.'
        }
      ]
    }
  },
  mounted() {
    // Start auto-carousel
    this.startCarousel();
    this.$nextTick(() => {
      // Simple measurement of the feature list height and slight adjustment of the carousel height
      this.measureHeightOnce();
    });
  },
  methods: {
    startCarousel() {
      this.intervalId = setInterval(() => {
        this.nextSlide();
      }, 4000);
    },
    prevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.carouselImages.length) % this.carouselImages.length;
    },
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.carouselImages.length;
    },
    measureHeightOnce() {
      // 检测设备宽度
      const isMobile = window.innerWidth <= 768;
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1200;
      
      if (isMobile) {
        // 移动设备使用固定高度
        const carouselContainer = document.querySelector('.carousel-container');
        if (carouselContainer) {
          carouselContainer.style.height = '600px'; // 增加移动设备高度，与原始HTML保持一致
        }
        return;
      }
      
      const featureList = document.querySelector('.feature-list');
      const carouselContainer = document.querySelector('.carousel-container');
      if(featureList && carouselContainer) {
        // 获取特性列表的高度并减去20px，使轮播图略小于特性列表
        const featureListHeight = featureList.offsetHeight - 20; 
        
        if (isTablet) {
          // 平板设备高度限制
          carouselContainer.style.height = Math.min(featureListHeight, 440) + 'px';
        } else {
          // 桌面设备
          carouselContainer.style.height = `${featureListHeight}px`;
        }
        
        // 添加窗口大小调整的事件监听器，以便在窗口大小变化时重新调整
        window.addEventListener('resize', this.resizeCarousel, { once: true });
      }
    },
    
    resizeCarousel() {
      // 窗口大小变化时，再次测量高度
      setTimeout(() => {
        this.measureHeightOnce();
      }, 300);
    }
  },
  beforeUnmount() {
    // 组件销毁前清除定时器和事件监听器
    clearInterval(this.intervalId);
    window.removeEventListener('resize', this.resizeCarousel);
  }
}
</script>

<style scoped>
.feature-section {
  padding: 80px 0;
  background-color: #f9fafc;
  background-image: radial-gradient(circle at 90% 10%, rgba(74, 107, 255, 0.1) 0%, rgba(249, 250, 252, 0) 40%);
  position: relative;
  overflow: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  font-size: 36px;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  font-weight: 700;
}

.section-title:after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #4a6bff, #2541b2);
  border-radius: 3px;
}

.feature-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 60px;
}

.carousel-wrapper {
  flex: 0 0 340px;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 20px;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 520px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  background-color: #f5f5f7;
  transition: height 0.3s ease;
}

.carousel-item {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.carousel-item.active {
  opacity: 1;
}

.carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

.slide-indicator {
  position: absolute;
  bottom: 15px;
  right: 15px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

.carousel-controls {
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
  padding: 0 15px;
  z-index: 2;
}

.prev-btn, .next-btn {
  background-color: rgba(255, 255, 255, 0.7);
  color: #1a1a2e;
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-radius: 50%;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
}

.prev-btn:hover, .next-btn:hover {
  background-color: white;
  transform: scale(1.1);
}

.carousel-dots {
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background-color: white;
  transform: scale(1.2);
}

.feature-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 25px;
  max-height: 600px; /* 限制特性列表高度，与轮播图高度匹配 */
  overflow-y: auto; /* 添加垂直滚动 */
  padding-right: 15px; /* 为滚动条留出空间 */
}

.feature-item {
  display: flex;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.feature-item:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(to bottom, #4a6bff, #2541b2);
}

.feature-icon {
  font-size: 28px;
  margin-right: 15px;
  padding-top: 5px;
}

.feature-content {
  flex: 1;
}

.feature-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.feature-description {
  font-size: 14px;
  line-height: 1.5;
  color: #555;
}

@media (max-width: 1200px) {
  .feature-content {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }
  
  .carousel-wrapper {
    width: 100%;
    max-width: 600px;
    position: static;
    margin-bottom: 30px;
  }
  
  .feature-list {
    width: 100%;
    max-width: 700px;
    max-height: none; /* 在垂直布局中取消高度限制 */
    overflow-y: visible;
    padding-right: 0;
    display: grid; /* 改为网格布局 */
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); /* 响应式网格 */
    gap: 20px;
  }
  
  .carousel-container {
    height: 440px;
  }
}

@media (max-width: 768px) {
  .feature-section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 30px;
  }
  
  .carousel-wrapper {
    width: 100%;
    max-width: 450px; /* 增加最大宽度让轮播图更突出 */
    margin: 0 auto 40px auto; /* 增加底部间距 */
    height: 600px; /* 增加高度与原始HTML一致 */
  }
  
  .carousel-container {
    max-width: 100%;
    height: 600px; /* 调整为与原始HTML一致的高度 */
    padding: 0;
    margin: 0 auto;
    border-radius: 12px;
  }
  
  .feature-item {
    padding: 15px;
  }
  
  .feature-icon {
    font-size: 24px;
  }
  
  .feature-title {
    font-size: 16px;
  }
  
  .feature-description {
    font-size: 13px;
  }
  
  .feature-list {
    padding: 0 15px;
    width: 100%;
    grid-template-columns: 1fr; /* 移动设备上只显示一列 */
  }
  
  .carousel-controls {
    padding: 0 8px; /* 减小内边距，按钮更靠近边缘 */
  }
  
  .prev-btn, .next-btn {
    width: 30px; /* 减小按钮尺寸 */
    height: 30px;
    font-size: 14px; /* 减小箭头尺寸 */
    opacity: 0.8; /* 稍微增加透明度 */
  }
  
  .slide-indicator {
    bottom: 10px;
    right: 10px;
    padding: 3px 8px;
    font-size: 10px;
  }
  
  .carousel-dots {
    bottom: 10px;
  }
  
  .dot {
    width: 6px;
    height: 6px;
  }
}

@media (max-width: 480px) {
  .feature-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 24px;
    margin-bottom: 30px;
  }
  
  .carousel-wrapper {
    max-width: 300px;
    height: 500px; /* 超小屏幕稍微降低高度 */
  }
  
  .carousel-container {
    height: 500px; /* 超小屏幕设备的高度 */
  }
  
  .feature-item {
    padding: 12px;
  }
  
  .feature-icon {
    font-size: 20px;
  }
  
  .prev-btn, .next-btn {
    width: 25px;
    height: 25px;
    font-size: 12px;
  }
}
</style> 